import { CustomButton } from '@/components';
import { Checkbox, Space, message } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import { useCallback, useEffect, useState } from 'react';
import { COCExportCounts } from './ShipmentHeader';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';
import ArrowBack from '@/assets/images/ArrowBack';
import styles from './ExportCOCDropdown.module.css';

type ExportOptionKey = 'statusUpdates' | 'comments' | 'alerts' | 'pods';

interface ExportCOCDropdownProps {
  isDetialedView?: boolean;
  cOCExportCounts?: COCExportCounts;
  trigger?: React.ReactNode;
  onExport: (payload: {
    exportOptions: Record<ExportOptionKey, boolean>;
  }) => void;
  canOpen?: () => boolean;
  fetchCOCCount?: () => void;
  onMissingClick?: () => void;
  onBackClick?: () => void;
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  contentOnly?: boolean;
  isFloatingToolbar?: boolean; // New prop to identify floating toolbar usage
}

interface ExportCheckboxItemProps {
  itemKey: ExportOptionKey;
  label: React.ReactNode;
  disabled?: boolean;
  checked: boolean;
  onChange: (key: ExportOptionKey, checked: boolean) => void;
  isFloatingToolbar?: boolean;
}

const ExportCheckboxItem: React.FC<ExportCheckboxItemProps> = ({
  itemKey,
  label,
  disabled = false,
  checked,
  onChange,
  isFloatingToolbar = false,
}) => {
  return (
    <div
      className={disabled ? styles.disabled : styles.option}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
    >
      <Checkbox
        disabled={disabled}
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          if (!disabled) {
            onChange(itemKey, e.target.checked);
          }
        }}
        className={`custom-dropdown-export-coc-checkbox export-coc-checkbox ${
          isFloatingToolbar ? 'floating-toolbar-checkbox' : ''
        } ${disabled ? 'checkbox-disabled' : 'checkbox-enabled'}`}
      >
        <p className={styles.label}>{label}</p>
      </Checkbox>
    </div>
  );
};

const ExportCOCDropdown = ({
  isDetialedView,
  cOCExportCounts,
  trigger,
  onExport,
  canOpen,
  fetchCOCCount,
  onMissingClick,
  onBackClick,
  visible,
  onVisibleChange,
  contentOnly = false,
  isFloatingToolbar = false,
}: ExportCOCDropdownProps) => {
  const getDefaultExportOptions = useCallback(
    () => ({
      statusUpdates: (cOCExportCounts?.status_pdf_count ?? 0) > 0,
      comments: false,
      alerts: false,
      pods: (cOCExportCounts?.pod_pdf_count ?? 0) > 0,
    }),
    [cOCExportCounts?.pod_pdf_count, cOCExportCounts?.status_pdf_count]
  );

  useEffect(() => {
    setExportOptions(getDefaultExportOptions());
  }, [cOCExportCounts?.pod_pdf_count, getDefaultExportOptions]);

  const [exportOptions, setExportOptions] = useState<
    Record<ExportOptionKey, boolean>
  >(getDefaultExportOptions());

  const handleChange = (key: ExportOptionKey, checked: boolean) => {
    setExportOptions((prev) => ({ ...prev, [key]: checked }));
  };

  const [internalVisible, setInternalVisible] = useState(false);
  const isVisible = visible !== undefined ? visible : internalVisible;
  const setVisible = onVisibleChange || setInternalVisible;

  // Reset export options back to defaults whenever dropdown closes / hides (including Back button)
  useEffect(() => {
    if (!isVisible) {
      setExportOptions(getDefaultExportOptions());
    }
  }, [isVisible, getDefaultExportOptions]);

  const handleVisibleChange = useCallback(
    (open: boolean) => {
      if (open && canOpen && !canOpen()) {
        message.error('Please select at least one item to export');
        setVisible(false);
        return;
      }
      if (open) {
        fetchCOCCount?.();
      }

      // Only update visibility, don't affect other states
      setVisible(open);
    },
    [canOpen, fetchCOCCount, setVisible]
  );
  // const missing_count =
  //   (cOCExportCounts?.selected_shipments || 0) -
  //   (cOCExportCounts?.shipments_with_pod || 0);
  const missing_count = cOCExportCounts?.missing_pods_count || 0;
  const selectedShipmentsCount = cOCExportCounts?.selected_shipments || 0;
  const isMultiSelect = selectedShipmentsCount > 1;
  const podCount = cOCExportCounts?.pod_pdf_count ?? 0;

  const topExportItems = [
    {
      key: 'statusUpdates',
      label: themeT('statusUpdates', theme.themeType),
      disable: !cOCExportCounts?.status_updates_exist,
      checked: true,
    },
    {
      key: 'comments',
      label: 'Comments',
      disable: !cOCExportCounts?.comments_exist,
    },
    {
      key: 'alerts',
      label: 'Alerts',
      disable: !cOCExportCounts?.alerts_exist,
    },
  ];

  const podLabelContent = () => {
    if (podCount === 0) {
      return 'PODs (None attached yet)';
    }

    if (isMultiSelect) {
      return (
        <span className={styles.pods}>
          PODs (Available on {podCount}{' '}
          {podCount === 1 ? 'shipment' : 'shipments'}
          {missing_count > 0 && (
            <>
              ,{' '}
              <span
                className={styles.missing}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onMissingClick?.();
                  setVisible(false);
                }}
              >
                {missing_count} missing
              </span>
            </>
          )}
          )
        </span>
      );
    }

    return 'PODs';
  };

  const podItem = {
    key: 'pods',
    label: podLabelContent(),
    disable: podCount === 0,
    checked: podCount > 0,
  };

  const menuContent = (
    <div
      className={`custom-ai-dropdown export-coc-dropdown ${styles.menu} ${isFloatingToolbar ? `floating-toolbar ${styles.floating}` : ''}`}
    >
      {onBackClick && (
        <div
          className={`mb-1 cursor-pointer ${isFloatingToolbar ? 'floating-toolbar-back' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onBackClick?.();
          }}
          onKeyDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onBackClick?.();
          }}
        >
          <ArrowBack fill='currentColor' />
        </div>
      )}

      <div className={`zl-para-10 mb-2 ${styles.heading}`}>
        {isMultiSelect
          ? `Include in export: ${selectedShipmentsCount} shipments`
          : 'Include in export:'}
      </div>

      <Space direction='vertical' className='w-full font-10'>
        {topExportItems.map((item) => (
          <ExportCheckboxItem
            key={item.key}
            itemKey={item.key as ExportOptionKey}
            label={item.label}
            disabled={item.disable}
            checked={exportOptions[item.key as ExportOptionKey]}
            onChange={handleChange}
            isFloatingToolbar={isFloatingToolbar}
          />
        ))}

        <div className={styles.divider} />

        <ExportCheckboxItem
          key={podItem.key}
          itemKey={podItem.key as ExportOptionKey}
          label={podItem.label}
          disabled={podItem.disable}
          checked={exportOptions.pods}
          onChange={handleChange}
          isFloatingToolbar={isFloatingToolbar}
        />
      </Space>

      <div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <CustomButton
          text={isDetialedView ? 'Download' : 'Export'}
          width='100%'
          fontWeight={'600'}
          className='alert-card-btn-text'
          height='28px'
          color='var(--export-button-text)'
          bgColor='var(--export-button-bg)'
          borderRadius={'8px'}
          marginTop='12px'
          boxShadow='none'
          onClick={() => {
            onExport({ exportOptions });
            setVisible(false);
            // Don't reopen main menu - just close the dropdown
          }}
          isFormValid={
            !Object.values(exportOptions).some((value) => value === true)
          }
        />
      </div>
    </div>
  );

  if (contentOnly) {
    return menuContent;
  }

  if (!trigger) return null;

  return (
    <>
      <Dropdown
        dropdownRender={() => menuContent}
        placement={isFloatingToolbar ? 'top' : 'bottomRight'}
        trigger={['click']}
        open={isVisible}
        onOpenChange={handleVisibleChange}
        getPopupContainer={(triggerNode) => triggerNode.parentElement!}
      >
        {trigger}
      </Dropdown>
    </>
  );
};

export { default as ExportCOCDropdownWrapper } from './ExportCOCDropdownWrapper';

export default ExportCOCDropdown;
